import { Tabs } from "antd";
import { useForm } from "antd/es/form/Form";
import { useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import CustomForm from "../../components/CustomForm";
import FormButtonCancel from "../../components/FormButtonCancel";
import FormButtonSave from "../../components/FormButtonSave";
import LockedModuleTooltip from "../../components/LockedModuleTooltip";
import useCheckModule from "../../hooks/useCheckModule";
import useUpdateAllData from "../../hooks/useUpdateAllData";
import { updateDeliverableRoles } from "../../services/api-server/deliverables";
import { updateProjectItem } from "../../services/api-server/projects";
import { updateWellExaminers } from "../../services/api-server/wellExamination";
import { getUserRole } from "../../services/api-server/roles_permissions";
import { useProjectStore } from "../../stores/projectStore";
import { alertMessage } from "../../utils/alert";
import Emitter from "../../utils/emitter";
import { getCrudaClass } from "../../utils/lookup_list";
import { HomeContext } from "../Home";

const WELLS_PROJECT_AREA = "Wells";

const examinerIdsOf = (project: any): string[] =>
	(project?.well_examiners || []).map((entry: any) => entry.user_id);

const sameIds = (a: string[], b: string[]) =>
	a.length === b.length && a.every((id) => b.includes(id));

/** B2B guests frequently have no Graph `mail`; their UPN is the fallback address. */
const examinerAddress = (user: any): string | null =>
	user?.mail || user?.userPrincipalName || null;

/** Picker options for the examiner field, labelled by the address we would store. */
const examinerOptions = (users: any[]) =>
	users.map((user) => ({ ...user, mail: examinerAddress(user) }));

const ProjectTeam = (props: any) => {
	const context: any = useContext(HomeContext);
	const queryClient = useQueryClient();
	const currentProject = props.currentProject;
	const [allData, setAllData] = useState<any>(props.data || {});

	const { updateAllData } = useUpdateAllData();

	const { project, setProject } = useProjectStore();

	const [formRef]: any = useForm();
	const [formReset, setFormReset] = useState<boolean>(true);
	const [initialForm, setInitialForm] = useState<any>(null);
	const [filteredDataOptions, setFilteredDataOptions] = useState<any>(null);
	const [tabKey, setTabKey] = useState<string>("roles-configuration");

	const { checkModule } = useCheckModule();

	const extraTabItems = [
		{ label: "Team roles", key: "roles-configuration" },
		{
			label: checkModule("time-writing") ? (
				"Time writing"
			) : (
				<LockedModuleTooltip>
					<div style={{ marginRight: "20px" }}>Time writing</div>
				</LockedModuleTooltip>
			),
			key: "timewriting_approval",
			disabled: !checkModule("time-writing"),
		},
	];

	useEffect(() => {
		if (currentProject) {
			let initialFormData: any = {
				project_manager: project?.project_manager,
				role_assignments: project?.role_assignments,
				general_members: project?.general_members,
				main_approver: project?.main_approver,
				secondary_approver: project?.secondary_approver,
				software: project?.software,
				exclude_timewriting: project?.exclude_timewriting,
				well_examiners: examinerIdsOf(project),
			};

			let usersData = {
				project_managers: [...(allData.users || [])],
				role_assignments: [...(allData.users || [])],
				general_members: [...(allData.users || [])],
				approvers: [...(allData.users || [])],
				well_examiners: examinerOptions(allData.users || []),
				softwares: [...(context.allData.software || [])],
				roles: [...(allData.roles || [])],
			};

			setAllData((prevData: any) => ({
				...prevData,
				...usersData,
			}));

			resetForm(initialFormData, true, { ...allData, ...usersData });
		}
	}, [tabKey]);

	const handleFormCancel = () => {
		if (currentProject) {
			const initialData = {
				project_manager: project?.project_manager,
				role_assignments: project?.role_assignments,
				general_members: project?.general_members,
				main_approver: project?.main_approver,
				secondary_approver: project?.secondary_approver,
				software: project?.software,
				exclude_timewriting: project?.exclude_timewriting,
				well_examiners: examinerIdsOf(project),
			};
			resetForm(initialData);
		} else {
			resetForm();
		}
	};

	const handleFormSave = (values: any, type: string) => {
		const unlinkRoleInDeliverables = (deletedRoles: any) => {
			deletedRoles.map((role: any) => {
				const roleId = role.role_id;
				updateDeliverableRoles(context?.currentTenantKey, roleId).catch(
					(error: any) => {
						console.log("Error of update deliverable roles", error);
					}
				);
			});
		};

		Object.keys(values).forEach((key) => {
			if (typeof values[key] == "string") {
				values[key] = values[key].trim();
			}
		});

		// Well examiners are saved through their own endpoint, never through the
		// generic project update (the server strips the field there anyway).
		const { well_examiners: examinerIds, ...projectValues } = values;
		const examinersChanged =
			tabKey === "roles-configuration" &&
			project?.project_area === WELLS_PROJECT_AREA &&
			Array.isArray(examinerIds) &&
			!sameIds(examinerIds, examinerIdsOf(project));

		const saveWellExaminers = (ids: string[]) => {
			const unresolved: string[] = [];
			const entries = ids.flatMap((id) => {
				const user = (allData.users || []).find((item: any) => item.id === id);
				const email = examinerAddress(user);
				if (!email) {
					unresolved.push(id);
					return [];
				}
				return [{ user_id: id, email }];
			});
			if (unresolved.length > 0) {
				alertMessage(
					"error",
					"Some selected well examiners have no email address or user principal name in Entra, so the examiner list was not saved."
				);
				return Promise.resolve();
			}
			return updateWellExaminers(
				context?.currentTenantKey,
				currentProject,
				entries
			)
				.then((updatedProject: any) => {
					setProject(updatedProject);
				})
				.catch((error: any) => {
					console.log("Error updating well examiners", error);
					alertMessage(
						"error",
						error?.error || "Failed to update well examiners."
					);
				});
		};

		Emitter.emit("loading", { name: "project-team", isLoading: true });

		getCrudaClass(tabKey).then((crudaClass: any) => {
			const form = crudaClass.getFormList(tabKey);
			const formList: any = [];
			form.forEach((element: any) => {
				if (element.children && !("name" in element)) {
					element.children.forEach((child_element: any) => {
						if (child_element.name) formList.push(child_element.name);
					});
				} else {
					if (element.name) formList.push(element.name);
				}
			});

			if (currentProject) {
				// console.log(
				// 	"values: ",
				// 	values,
				// 	"Initial form",
				// 	initialForm,
				// 	"formList",
				// 	formList
				// );
				updateProjectItem(
					context?.currentTenantKey,
					currentProject,
					projectValues,
					initialForm,
					props.user,
					formList
				)
					.then((updatedItem: any) => {
						setProject(updatedItem);
						if (examinersChanged) {
							saveWellExaminers(examinerIds);
						}
						let project = context.allData.projects.find(
							(item: any) => item.project_code == currentProject
						);
						project.role_assignments = values?.role_assignments;
						context.allData.projects = context.allData.projects.map(
							(item: any) =>
								item.project_code == currentProject ? updatedItem : item
						);
						allData.projects = allData.projects.map((item: any) =>
							item.project_code == currentProject ? updatedItem : item
						);
						setAllData(allData);

						alertMessage("success", "Project has been successfully updated.");

						resetForm(values);
						props?.handleProjectConfigSave();

						if (tabKey === "roles-configuration") {
							const missingRoles =
								values?.role_assignments?.filter((role: any) => {
									if (context.allData?.roles) {
										return !context.allData?.roles.find(
											(assignment: any) => assignment?._id == role?.role_id
										);
									}
								}) || [];

							// compare initial data and values and get the removed roles from initial data if any
							const removedRoles =
								initialForm?.role_assignments?.filter(
									(role: any) =>
										!values?.role_assignments?.find(
											(assignment: any) => assignment?.role_id == role?.role_id
										)
								) || [];

							// combine missing roles and removed roles
							removedRoles?.push(...missingRoles);
							unlinkRoleInDeliverables(removedRoles);
						}
						updateAllData(updatedItem, "update", "projects");
					})
					.finally(() => {
						Emitter.emit("loading", {
							name: "project-team",
							isLoading: false,
						});
					});
			}
		});

		getUserRole(context?.currentTenantKey, props.user.email)
			.then((data: any) => {
				context.setUserRole(data);
				// Invalidate role permissions cache to ensure fresh data
				queryClient.invalidateQueries({
					queryKey: ["roles", context?.currentTenantKey],
				});
				queryClient.invalidateQueries({
					queryKey: ["userRole", context?.currentTenantKey],
				});
			})
			.catch((error) => {
				console.log("Fail to update user role!", error);
			});
	};

	const resetForm = (
		values: any = null,
		reset: any = true,
		_allData: any = null
	) => {
		if (formRef) {
			setFormReset(true);
			formRef.resetFields();
			formRef.setFieldsValue(values);

			if (values) {
				const data = { ...(_allData || allData) };
				if (values["general_members"] && data["general_members"]) {
					data["general_members"] = data["general_members"].filter(
						(member: any) => !values["general_members"]?.includes(member.mail)
					);
				}
				if (tabKey === "timewriting_approval") {
					data["approvers"] = data["approvers"].filter(
						(member: any) =>
							!values["main_approver"]?.includes(member.mail) &&
							!values["secondary_approver"]?.includes(member.mail)
					);
				}
				setFilteredDataOptions({ ...data });
			}
			formRef.setFieldsValue(values);
		}
	};

	const changeExtraTabContent = (tabKey: string) => {
		switch (tabKey) {
			case "roles-configuration":
				return (
					<CustomForm
						setFormReset={setFormReset}
						tabKey={"roles-configuration"}
						formOption={project?.project_area}
						formRef={formRef}
						dataOptions={allData}
						filteredDataOptions={filteredDataOptions || allData}
						readOnly={props.readOnly}
					/>
				);
			case "timewriting_approval":
				return (
					<CustomForm
						setFormReset={setFormReset}
						tabKey={"timewriting_approval"}
						formRef={formRef}
						dataOptions={allData}
						filteredDataOptions={filteredDataOptions || allData}
						readOnly={props.readOnly}
					/>
				);
			default:
				return <>No content</>;
		}
	};

	return (
		<div
			className="main-container"
			style={{
				width: "70%",
				display: "flex",
				flexDirection: "column",
				borderRadius: "3px",
			}}
		>
			<div className="generic-header" style={{ height: "50px" }}>
				Settings
			</div>
			<div
				className="generic-content"
				style={{
					flex: "1",
					gap: "0px",
					maxHeight: "100%",
					overflow: "hidden",
					flexWrap: "nowrap",
				}}
			>
				<Tabs
					tabPosition="left"
					activeKey={tabKey}
					onChange={(key: any) => setTabKey(key)}
					items={extraTabItems}
				/>
				<div
					style={{
						flex: "1",
						height: "100%",
						display: "flex",
						maxWidth: "100%",
						overflow: "auto",
						flexDirection: "column",
					}}
				>
					{changeExtraTabContent(tabKey)}
				</div>
			</div>
			{!props.readOnly && (
				<div className="generic-footer">
					<>
						<FormButtonCancel
							form={formRef}
							handleFormCancel={handleFormCancel}
							formReset={formReset}
						></FormButtonCancel>
						<FormButtonSave
							form={formRef}
							setInitialForm={setInitialForm}
							handleFormSave={handleFormSave}
							formReset={formReset}
						></FormButtonSave>
					</>
				</div>
			)}
		</div>
	);
};

export default ProjectTeam;
